import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError as formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { fmtIST } from "@/lib/datetime";
import { MagnifyingGlass, User, Buildings, Car, IdentificationCard, ArrowRight, Check, Lightning, CaretUpDown, UserPlus } from "@phosphor-icons/react";
import CameraCapture from "@/components/gate-check/CameraCapture";
import QRScanner from "@/components/gate-check/QRScanner";

const emptyForm = {
    visitor_name: "",
    visitor_phone: "",
    visitor_email: "",
    company: "",
    id_number: "",
    purpose: "",
    host_id: "",
    gate_id: "",
    vehicle_number: "",
    expected_duration_minutes: "",
    pre_approved: true,
    host_user_id: "",
    visit_type: "",
};

export default function GateCheckIn() {
    const nav = useNavigate();
    const { user } = useAuth();
    // "Add host" (external directory) is a privileged action — mirrors the
    // manage_gates_hosts capability already used on the Admin Hosts page.
    const canAddHost = ["admin", "admin_addl"].includes(user?.role)
        || (user?.capabilities || []).includes("manage_gates_hosts");
    const [gates, setGates] = useState([]);
    const [hosts, setHosts] = useState([]);           // external directory hosts (legacy)
    const [userDirectory, setUserDirectory] = useState([]); // Ops IQ users as hosts
    const [hostOpen, setHostOpen] = useState(false);
    const [addHostOpen, setAddHostOpen] = useState(false);
    const [visitTypes, setVisitTypes] = useState([]);
    const [manageTypesOpen, setManageTypesOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [photo, setPhoto] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    // Pre-approval pass captured from a QR scan — when set, submit calls the
    // /checkin-from-pass endpoint instead of the walk-in check-in.
    const [scannedPass, setScannedPass] = useState(null);
    // Debounced phone lookup — as the guard types the phone, we surface the
    // last-known visitor (photo + name + company) so they can 1-click prefill.
    const [priorMatch, setPriorMatch] = useState(null);
    const phoneDebounceRef = useRef(null);

    useEffect(() => {
        (async () => {
            const [g, h, dir] = await Promise.all([
                api.get("/gate-check/gates"),
                api.get("/gate-check/hosts"),
                api.get("/gate-check/hosts/directory"),
            ]);
            setGates(g.data);
            setHosts(h.data);
            setUserDirectory(dir.data);
            if (g.data.length > 0) setForm((f) => ({ ...f, gate_id: g.data[0].id }));
        })();
    }, []);

    const loadVisitTypes = async () => {
        try {
            const { data } = await api.get("/gate-check/lookups/visit-types");
            setVisitTypes(data?.values || []);
        } catch { /* silent */ }
    };
    useEffect(() => { loadVisitTypes(); }, []);

    // host_ref encodes which kind of host is picked: "user:{id}" (Ops IQ user,
    // preferred) or "host:{id}" (external directory host).
    const hostRef = form.host_user_id
        ? `user:${form.host_user_id}`
        : form.host_id
            ? `host:${form.host_id}`
            : "";
    const selectedUserHost = userDirectory.find((u) => u.id === form.host_user_id);
    const selectedDirHost = hosts.find((h) => h.id === form.host_id);

    const pickHost = (ref) => {
        if (ref.startsWith("user:")) {
            setForm((f) => ({ ...f, host_user_id: ref.slice(5), host_id: "" }));
        } else {
            setForm((f) => ({ ...f, host_id: ref.slice(5), host_user_id: "" }));
        }
        setHostOpen(false);
    };

    const onHostCreated = (h) => {
        setHosts((xs) => [...xs, h]);
        setForm((f) => ({ ...f, host_id: h.id, host_user_id: "" }));
        toast.success(`Host added: ${h.name}`);
    };

    // Debounced repeat-visitor lookup keyed on phone (7+ digits).
    useEffect(() => {
        const phone = (form.visitor_phone || "").trim();
        if (phoneDebounceRef.current) clearTimeout(phoneDebounceRef.current);
        const digits = phone.replace(/\D/g, "");
        if (digits.length < 7) { setPriorMatch(null); return; }
        // Skip if the current form was already prefilled from this exact prior match.
        phoneDebounceRef.current = setTimeout(async () => {
            try {
                const { data } = await api.get(`/gate-check/visits/search?phone=${encodeURIComponent(phone)}`);
                if (data && data.length > 0) {
                    // Newest first (backend already sorts by check_in_at desc).
                    setPriorMatch(data[0]);
                } else {
                    setPriorMatch(null);
                }
            } catch { /* silent — background lookup */ }
        }, 450);
        return () => phoneDebounceRef.current && clearTimeout(phoneDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.visitor_phone]);

    const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

    const applyScannedPass = async (rawScanned) => {
        // Accepts either a full URL (https://.../pass/XYZ) or a bare pass id.
        let passId = String(rawScanned || "").trim();
        const m = passId.match(/\/pass\/([A-Z0-9]+)/i);
        if (m) passId = m[1];
        if (!passId) return;
        try {
            const { data } = await api.get(`/gate-check/passes/${passId}`);
            if (data.status !== "active") {
                toast.error(`Pass is ${data.status}. Cannot use for check-in.`);
                return;
            }
            setScannedPass(data);
            setForm((f) => ({
                ...f,
                visitor_name: data.visitor_name || "",
                visitor_phone: data.visitor_phone || "",
                company: data.company || "",
                purpose: data.purpose || "",
                visit_type: data.visit_type || f.visit_type,
                host_id: "", // host is baked into the pass; not shown as a picker choice
                gate_id: f.gate_id, // guard may want to override
                pre_approved: true,
            }));
            toast.success(`Pre-approved · ${data.visitor_name}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Pass not found");
        }
    };

    const clearScannedPass = () => {
        setScannedPass(null);
        setForm({ ...form, pre_approved: true });
    };

    const doSearch = async () => {
        if (!searchTerm.trim()) return;
        // If the scanned/pasted value looks like a pre-approval pass URL or ID,
        // try the pass endpoint first — much richer UX than a plain visit search.
        const raw = searchTerm.trim();
        if (/\/pass\/[A-Z0-9]+/i.test(raw) || /^[A-Z0-9]{10,20}$/i.test(raw)) {
            await applyScannedPass(raw);
            setSearchTerm("");
            return;
        }
        try {
            const { data } = await api.get(`/gate-check/visits/search?q=${encodeURIComponent(raw)}`);
            setSearchResults(data);
            if (data.length === 0) toast.info("No prior visits found. Register as walk-in.");
        } catch (e) { toast.error("Search failed"); }
    };

    const prefillFromVisit = (v) => {
        setForm({
            ...form,
            visitor_name: v.visitor_name || "",
            visitor_phone: v.visitor_phone || "",
            visitor_email: v.visitor_email || "",
            company: v.company || "",
            id_number: v.id_number || "",
            purpose: v.purpose || "",
            host_id: v.host_id || form.host_id,
            host_user_id: v.host_user_id || "",
            vehicle_number: v.vehicle_number || "",
            pre_approved: true,
        });
        setSearchResults([]);
        setPriorMatch(null);
        toast.success(`Loaded prior visit for ${v.visitor_name}`);
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.gate_id) {
            toast.error("Select a gate before checking in");
            return;
        }
        setSubmitting(true);
        try {
            let data;
            if (scannedPass) {
                // Pre-approved flow — thin payload, backend pulls the rest from the pass.
                const payload = {
                    pass_id: scannedPass.pass_id,
                    photo_base64: photo,
                    vehicle_number: form.vehicle_number || null,
                    id_number: form.id_number || null,
                    gate_id: form.gate_id || null,
                };
                ({ data } = await api.post("/gate-check/visits/checkin-from-pass", payload));
            } else {
                if (!form.visitor_name || !form.purpose || !form.visit_type) {
                    toast.error("Fill name, visit type and purpose");
                    setSubmitting(false);
                    return;
                }
                if (!form.visitor_phone && form.visit_type !== "Buyer / Client") {
                    toast.error("Phone is required (optional only for Buyer / Client visits)");
                    setSubmitting(false);
                    return;
                }
                const payload = {
                    ...form,
                    photo_base64: photo,
                    expected_duration_minutes: form.expected_duration_minutes ? Number(form.expected_duration_minutes) : null,
                };
                ({ data } = await api.post("/gate-check/visits/checkin", payload));
            }
            toast.success(`Checked in ${data.visitor_name}`);
            nav(`/checkpoint/pass/${data.id}`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Check-in failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <AddHostDialog open={addHostOpen} onClose={() => setAddHostOpen(false)} onCreated={onHostCreated} />
            <ManageVisitTypesDialog
                open={manageTypesOpen}
                onClose={() => setManageTypesOpen(false)}
                values={visitTypes}
                onSaved={(next) => {
                    setVisitTypes(next);
                    setForm((f) => (f.visit_type && !next.includes(f.visit_type) ? { ...f, visit_type: "" } : f));
                }}
            />
            <PageHeader
                overline="Gate Ops · Live Entry"
                title="Visitor Check-In"
                subtitle="Scan-first workflow. Capture visitor photo, ID and host details in under 60 seconds."
            />
            <div className="p-6 grid grid-cols-1 xl:grid-cols-5 gap-6">
                {/* LEFT: Scan + Camera */}
                <div className="xl:col-span-2 space-y-6">
                    <Card className="tech-card p-5">
                        <div className="overline mb-2">Step 1 · Lookup</div>
                        <div className="flex gap-2">
                            <div className="relative flex-1">
                                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input
                                    placeholder="Phone, name, or visit ID"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                                    data-testid="input-search-visitor"
                                    className="h-11 pl-9"
                                />
                            </div>
                            <Button
                                onClick={doSearch}
                                data-testid="btn-search-visitor"
                                className="h-11 bg-brand hover:bg-brand/90 text-white"
                            >
                                Search
                            </Button>
                        </div>
                        {searchResults.length > 0 && (
                            <div className="mt-3 border border-border rounded-md divide-y divide-border overflow-hidden">
                                {searchResults.map((r) => (
                                    <button
                                        key={r.id}
                                        onClick={() => prefillFromVisit(r)}
                                        data-testid={`btn-prefill-${r.id}`}
                                        className="w-full text-left px-3 py-2.5 hover:bg-accent flex items-center justify-between text-sm"
                                    >
                                        <div>
                                            <div className="font-semibold">{r.visitor_name}</div>
                                            <div className="text-xs text-muted-foreground">
                                                {r.visitor_phone} · {r.company || "—"}
                                            </div>
                                        </div>
                                        <ArrowRight size={16} />
                                    </button>
                                ))}
                            </div>
                        )}
                    </Card>

                    <Card className="tech-card p-5">
                        <div className="overline mb-2">Step 2 · Scan QR pass</div>
                        <QRScanner onScan={(v) => { applyScannedPass(v); }} />
                        <div className="text-[10.5px] text-muted-foreground mt-2">
                            Scan the visitor's pre-approval QR pass to auto-fill the check-in form. For old walk-in QR codes, use the search box above.
                        </div>
                    </Card>

                    <Card className="tech-card p-5">
                        <div className="overline mb-2">Step 3 · Capture Photo <span className="text-muted-foreground normal-case tracking-normal font-normal">(optional)</span></div>
                        <CameraCapture onCapture={setPhoto} />
                        <div className="text-[10.5px] text-muted-foreground mt-2">
                            Skippable — check-in works without a photo. Capture when the visitor is comfortable or your policy requires it.
                        </div>
                    </Card>
                </div>

                {/* RIGHT: Form */}
                <form onSubmit={submit} className="xl:col-span-3 space-y-6" data-testid="checkin-form">
                    {scannedPass && (
                        <Card className="p-4 border-2 border-emerald-500 bg-emerald-50/60" data-testid="scanned-pass-banner">
                            <div className="flex items-start gap-3">
                                <div className="h-10 w-10 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                                    <Lightning size={20} weight="fill" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] tracking-widest uppercase font-semibold text-emerald-700">Pre-approved pass · 1-tap check-in</div>
                                    <div className="font-bold text-slate-800 truncate">{scannedPass.visitor_name}</div>
                                    <div className="text-[12px] text-slate-600 truncate">
                                        {scannedPass.visit_type ? `${scannedPass.visit_type} · ` : ""}{scannedPass.company || "Individual"} · Host: {scannedPass.host_name || "—"} · Purpose: {scannedPass.purpose}
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-1 font-mono">{scannedPass.pass_id}</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={clearScannedPass}
                                    className="text-[11px] text-slate-500 hover:text-rose-600 underline"
                                    data-testid="clear-scanned-pass"
                                >
                                    Clear
                                </button>
                            </div>
                        </Card>
                    )}
                    <Card className="tech-card p-5">
                        <div className="overline mb-4">Step 4 · Visitor Details</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Full Name" required>
                                <Input value={form.visitor_name} onChange={set("visitor_name")}
                                    data-testid="input-visitor-name" className="h-11" />
                            </Field>
                            <Field
                                label={form.visit_type === "Buyer / Client" ? "Phone (optional for Buyer / Client)" : "Phone"}
                                required={form.visit_type !== "Buyer / Client"}
                            >
                                <Input value={form.visitor_phone} onChange={set("visitor_phone")}
                                    placeholder={form.visit_type === "Buyer / Client" ? "Optional for Buyer / Client" : "+91 98xxxx"}
                                    data-testid="input-visitor-phone" className="h-11" />
                                {form.visit_type === "Buyer / Client" && (
                                    <div className="text-[10.5px] text-muted-foreground mt-1">Phone &amp; email are masked from non-admin users for Buyer / Client visits.</div>
                                )}
                                {priorMatch && !scannedPass && priorMatch.visitor_phone === form.visitor_phone && (
                                    <button
                                        type="button"
                                        onClick={() => prefillFromVisit(priorMatch)}
                                        className="mt-2 w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-sky-200 bg-sky-50 hover:bg-sky-100 text-left"
                                        data-testid="prior-visitor-hint"
                                    >
                                        <div className="h-7 w-7 rounded-full bg-sky-500 text-white flex items-center justify-center text-[11px] font-bold shrink-0">
                                            {(priorMatch.visitor_name || "?")[0]?.toUpperCase()}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[12px] font-semibold text-sky-900 truncate">
                                                Prior visitor · {priorMatch.visitor_name}
                                            </div>
                                            <div className="text-[10.5px] text-sky-700 truncate">
                                                {priorMatch.company || "—"} · last visit {fmtIST(priorMatch.check_in_at, { date: true, time: false })}
                                            </div>
                                        </div>
                                        <ArrowRight size={14} className="text-sky-700 shrink-0" />
                                    </button>
                                )}
                            </Field>
                            <Field label="Email">
                                <Input type="email" value={form.visitor_email} onChange={set("visitor_email")}
                                    data-testid="input-visitor-email" className="h-11" />
                            </Field>
                            <Field label="Company">
                                <Input value={form.company} onChange={set("company")}
                                    data-testid="input-visitor-company" className="h-11" />
                            </Field>
                            <Field label="ID / Aadhaar / PAN">
                                <Input value={form.id_number} onChange={set("id_number")}
                                    data-testid="input-visitor-id" className="h-11" />
                            </Field>
                            <Field label="Vehicle Number">
                                <Input value={form.vehicle_number} onChange={set("vehicle_number")}
                                    placeholder="KA-01-AB-1234" data-testid="input-vehicle-number" className="h-11" />
                            </Field>
                            <div className="md:col-span-2">
                                <Field label={
                                    <span className="flex items-center gap-2">
                                        <span>Visit Type</span>
                                        <span className="text-brand">*</span>
                                        {canAddHost && (
                                            <button
                                                type="button"
                                                onClick={() => setManageTypesOpen(true)}
                                                className="text-[10.5px] font-mono tracking-wider uppercase text-brand hover:text-brand-dark hover:underline"
                                                data-testid="visit-type-manage"
                                            >
                                                Manage
                                            </button>
                                        )}
                                    </span>
                                }>
                                    <Select value={form.visit_type} onValueChange={(v) => setForm({ ...form, visit_type: v })}>
                                        <SelectTrigger className="h-11" data-testid="select-visit-type">
                                            <SelectValue placeholder="Select visit type…" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {visitTypes.map((v) => (
                                                <SelectItem key={v} value={v} data-testid={`option-visit-type-${v}`}>{v}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </div>
                            <div className="md:col-span-2">
                                <Field label="Purpose of Visit" required>
                                    <Textarea rows={2} value={form.purpose} onChange={set("purpose")}
                                        data-testid="input-purpose" placeholder="Meeting, delivery, interview…" />
                                </Field>
                            </div>
                        </div>
                    </Card>

                    <Card className="tech-card p-5">
                        <div className="overline mb-4">Step 5 · Host & Gate</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Field label="Host / Employee">
                                <Popover open={hostOpen} onOpenChange={setHostOpen}>
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            className="w-full h-11 px-3 rounded-md border border-input bg-white flex items-center justify-between text-sm hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand"
                                            data-testid="select-host"
                                        >
                                            <span className={hostRef ? "text-slate-800 truncate" : "text-muted-foreground"}>
                                                {selectedUserHost
                                                    ? selectedUserHost.name
                                                    : selectedDirHost
                                                        ? selectedDirHost.name
                                                        : "Select host…"}
                                            </span>
                                            <CaretUpDown size={14} className="text-slate-400 shrink-0" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className="w-[--radix-popover-trigger-width] p-0"
                                        align="start"
                                        onWheel={(e) => e.stopPropagation()}
                                        onTouchMove={(e) => e.stopPropagation()}
                                        onOpenAutoFocus={(e) => e.preventDefault()}
                                    >
                                        <Command>
                                            <CommandInput placeholder="Search host by name…" data-testid="host-search" />
                                            <CommandList
                                                className="max-h-[280px] overflow-y-auto overscroll-contain touch-pan-y"
                                                onWheel={(e) => e.stopPropagation()}
                                                onTouchMove={(e) => e.stopPropagation()}
                                            >
                                                <CommandEmpty>No matching host</CommandEmpty>
                                                <CommandGroup heading="Ops IQ Users">
                                                    {userDirectory.map((u) => (
                                                        <CommandItem
                                                            key={u.id}
                                                            value={u.name}
                                                            onSelect={() => pickHost(`user:${u.id}`)}
                                                            data-testid={`option-host-user-${u.id}`}
                                                        >
                                                            <div className="flex-1 min-w-0 text-[13px] font-medium text-slate-800 truncate">{u.name}</div>
                                                            {form.host_user_id === u.id && <Check size={14} className="text-brand shrink-0 ml-2" />}
                                                        </CommandItem>
                                                    ))}
                                                </CommandGroup>
                                                {hosts.length > 0 && (
                                                    <CommandGroup heading="External Hosts">
                                                        {hosts.map((h) => (
                                                            <CommandItem
                                                                key={h.id}
                                                                value={h.name}
                                                                onSelect={() => pickHost(`host:${h.id}`)}
                                                                data-testid={`option-host-${h.id}`}
                                                            >
                                                                <div className="flex-1 min-w-0 text-[13px] font-medium text-slate-800 truncate">{h.name}</div>
                                                                {form.host_id === h.id && <Check size={14} className="text-brand shrink-0 ml-2" />}
                                                            </CommandItem>
                                                        ))}
                                                    </CommandGroup>
                                                )}
                                                {canAddHost && (
                                                    <CommandGroup>
                                                        <CommandItem
                                                            value="add-new-host"
                                                            onSelect={() => { setHostOpen(false); setAddHostOpen(true); }}
                                                            className="text-brand font-semibold"
                                                            data-testid="host-add-new"
                                                        >
                                                            <UserPlus size={14} className="mr-2" /> Add new external host…
                                                        </CommandItem>
                                                    </CommandGroup>
                                                )}
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                            </Field>
                            <Field label="Gate" required>
                                <Select value={form.gate_id || ""} onValueChange={(v) => setForm({ ...form, gate_id: v })}>
                                    <SelectTrigger className="h-11" data-testid="select-gate">
                                        <SelectValue placeholder="Select gate…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {gates.map((g) => (
                                            <SelectItem key={g.id} value={g.id} data-testid={`option-gate-${g.id}`}>
                                                {g.name} · {g.location}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Expected Duration (minutes) — optional">
                                <Input type="number" min={5} value={form.expected_duration_minutes}
                                    onChange={(e) => setForm({ ...form, expected_duration_minutes: e.target.value })}
                                    placeholder="e.g. 60"
                                    data-testid="input-duration" className="h-11" />
                            </Field>
                            <div className="flex items-end pb-1 gap-3">
                                <Switch
                                    id="pre-approved"
                                    checked={form.pre_approved}
                                    onCheckedChange={(v) => setForm({ ...form, pre_approved: v })}
                                    data-testid="switch-preapproved"
                                />
                                <Label htmlFor="pre-approved" className="text-sm cursor-pointer">
                                    Pre-approved visitor (turn off to route for host approval)
                                </Label>
                            </div>
                        </div>
                    </Card>

                    <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm pt-4 pb-2 border-t border-border">
                        <div className="flex items-center justify-between gap-3">
                            <div className="text-xs text-muted-foreground">
                                Photo: {photo ? "✓ Captured" : "— none —"}
                            </div>
                            <div className="flex gap-2">
                                <Button type="button" variant="outline" onClick={() => { setForm(emptyForm); setPhoto(null); setSearchResults([]); }}
                                    data-testid="btn-clear-form" className="h-12 px-6">
                                    Clear
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="btn-checkin-submit"
                                    className="h-12 px-8 bg-brand hover:bg-brand/90 text-white text-base font-semibold press-effect"
                                >
                                    <Check size={18} className="mr-2" weight="bold" />
                                    {submitting ? "Checking in…" : "Check-in Visitor"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <div>
            <Label className="overline text-[10px] mb-1.5 block">
                {label} {required && <span className="text-brand">*</span>}
            </Label>
            {children}
        </div>
    );
}

// ─────────── Admin-only: manage the Visit Type catalog ───────────
function ManageVisitTypesDialog({ open, onClose, values, onSaved }) {
    const [items, setItems] = useState(values || []);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (open) { setItems(values || []); setInput(""); } }, [open, values]);

    const add = () => {
        const s = input.trim();
        if (!s) return;
        if (items.some((v) => v.toLowerCase() === s.toLowerCase())) {
            toast.info("Already in the list"); return;
        }
        setItems((xs) => [...xs, s]);
        setInput("");
    };
    const remove = (v) => setItems((xs) => xs.filter((x) => x !== v));
    const move = (idx, dir) => {
        setItems((xs) => {
            const next = [...xs];
            const j = idx + dir;
            if (j < 0 || j >= next.length) return xs;
            [next[idx], next[j]] = [next[j], next[idx]];
            return next;
        });
    };
    const save = async () => {
        if (!items.length) { toast.error("Keep at least one visit type"); return; }
        setBusy(true);
        try {
            const { data } = await api.put("/gate-check/lookups/visit-types", { values: items });
            toast.success("Visit types updated");
            onSaved?.(data.values || items);
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="manage-visit-types-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <User className="text-brand" size={18} /> Manage Visit Types
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                    <div className="text-[12px] text-muted-foreground">
                        Admin-managed list powering the Visit Type dropdown on the check-in form. Order shown here is the order guards see.
                    </div>
                    <div className="rounded-md border border-slate-200 divide-y divide-slate-100 max-h-[280px] overflow-y-auto">
                        {items.length === 0 && (
                            <div className="p-3 text-[12px] text-slate-400 italic">No visit types yet — add one below.</div>
                        )}
                        {items.map((v, i) => (
                            <div key={v} className="flex items-center gap-2 px-3 py-2" data-testid={`vt-row-${i}`}>
                                <div className="flex-1 text-[13px] font-medium text-slate-800">{v}</div>
                                <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                                        className="text-[11px] font-mono text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up">▲</button>
                                <button type="button" onClick={() => move(i, +1)} disabled={i === items.length - 1}
                                        className="text-[11px] font-mono text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down">▼</button>
                                <button type="button" onClick={() => remove(v)}
                                        className="text-[11px] font-semibold text-rose-500 hover:text-rose-700"
                                        data-testid={`vt-remove-${i}`}>Remove</button>
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Input
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                            placeholder="Add a new type (e.g. Internship Visit)"
                            className="h-10"
                            data-testid="vt-input"
                        />
                        <Button type="button" onClick={add} disabled={!input.trim()} className="h-10 bg-brand hover:bg-brand/90 text-white" data-testid="vt-add">
                            Add
                        </Button>
                    </div>
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                    <Button type="button" onClick={save} disabled={busy} className="bg-brand hover:bg-brand/90 text-white" data-testid="vt-save">
                        {busy ? "Saving…" : "Save changes"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ─────────── Privileged: quick-add an external (non-Ops-IQ) host ───────────
function AddHostDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", department: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (open) setForm({ name: "", email: "", phone: "", department: "" }); }, [open]);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { toast.error("Host name is required"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/gate-check/hosts", {
                name: form.name.trim(),
                email: form.email.trim() || null,
                phone: form.phone.trim() || null,
                department: form.department.trim() || null,
            });
            onCreated?.(data);
            onClose?.();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add host");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="add-host-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserPlus className="text-brand" size={18} /> Add External Host
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                    <div className="text-[12px] text-muted-foreground">
                        For hosts who aren't Ops IQ users. Existing employees should be picked from the Ops IQ Users section instead.
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Field label="Name" required>
                            <Input value={form.name} onChange={set("name")} data-testid="addhost-name" className="h-10" />
                        </Field>
                        <Field label="Department">
                            <Input value={form.department} onChange={set("department")} data-testid="addhost-department" className="h-10" />
                        </Field>
                        <Field label="Email">
                            <Input type="email" value={form.email} onChange={set("email")} data-testid="addhost-email" className="h-10" />
                        </Field>
                        <Field label="Phone">
                            <Input value={form.phone} onChange={set("phone")} data-testid="addhost-phone" className="h-10" />
                        </Field>
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
                        <Button type="submit" disabled={busy} className="bg-brand hover:bg-brand/90 text-white" data-testid="addhost-save">
                            {busy ? "Saving…" : "Add host"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
